import { ReactNode } from 'react';
import {
  Badge,
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
} from '@fluentui/react-components';
import { Hammer, RefreshCw } from 'lucide-react';

import { Migration } from '../api/migrations';
import {
  PpkgStatus,
  isOperatorRetryablePpkgError,
  isPpkgServable,
  ppkgConflictDetail,
  ppkgErrorCode,
} from '../api/ppkg';
import { usePpkgBuild } from '../hooks/usePpkgBuild';
import RelativeTime from './RelativeTime';
import { errorText } from '../lib/errorText';
import { STROKE, TEXT } from '../theme/tokens';

// FD-321 (FD-ADR-034) — the provisioning package, inside the Wave panel.
//
// Design direction: PRECISION INSTRUMENT, and specifically an INLINE STATE ROW
// rather than a dialog. This is not a new feature area; it is the second half of
// a wave's readiness. An operator's question is "is this wave deliverable", and
// the answer must be legible at rest, with no click — because the thing it
// gates happens PAST THE POINT OF NO RETURN. A device that reaches
// `PSH-PPKG` with no package in its plan does not fail politely: it throws in
// the committed zone, after `PSH-AADLEAVE`, and is salvaged. `409
// PACKAGE_NOT_READY` is what stops that, and this row is how a human sees it
// coming.
//
// A build takes no operator input, so there is no confirmation dialog on the
// happy path — a modal that asks nothing and says nothing is a click for its own
// sake. Refusals render inline, where the next action is.
//
// ── NAMING: THE SERVICE IS AIRBRIDGE'S; THE ESCALATION IS RUBIX'S (FD-501) ──
//
// Operator-facing copy here names **the AirBridge build service** for the thing
// that builds the package, and **Rubix support** for the people you raise a
// problem with. The console is AirBridge (FD-ADR-043 D1) and the build service
// is a component of that product; Rubix is the company, and "raise it with
// Rubix support" is an instruction with an addressee, so it keeps the company
// name.
//
// A blanket rename would have broken two sentences outright — "the package
// identifier AirBridge holds is not the one AirBridge recorded" says nothing —
// because several refusals exist precisely to distinguish WHAT THIS DEPLOYMENT
// HOLDS from WHAT THE BUILD SERVICE RECORDED. That distinction is the content of
// the message, not decoration.
//
// What this does NOT do is hide where the build runs. The package is built on
// Rubix-operated hardware and the wave's join credential transits it in flight;
// that is FD-ADR-034 C3, an owner-approved concession recorded in full in
// `docs/SECURITY_OVERVIEW.md`, which is the customer-facing artifact for it. The
// console never claimed the build was local and does not claim it now.
//
// ── WHY EACH REFUSAL GETS ITS OWN SENTENCE ─────────────────────────────────
//
// Every code below answers exactly two questions, because those are the only two
// an operator needs: DID ANYTHING HAPPEN, and IS RETRY THE RECOVERY. Several
// pairs look alike and demand opposite actions:
//
//   * `PPKG_WORKER_UNAVAILABLE` asserts nothing happened  → retry is safe.
//   * `PPKG_UNKNOWN` asserts nothing at all               → a package MAY exist.
//   * `PPKG_RATE_LIMITED` is a ceiling                    → retry LATER, not now.
//   * `REBUILD_LIMIT_EXCEEDED` is not capacity            → a person, not a retry.
//   * `BPRT_*` point at ACTIVATE WAVE, not at this button.
//
// A retry button therefore appears only where retrying is genuinely the
// recovery. Its absence on the others is the design, not an omission — and the
// copy says so, because an operator who cannot see why a retry is missing will
// reload the page and click again.

export interface WavePackagePanelProps {
  migration: Migration;
  /** Invalidate the migration — package state changed. */
  onDone?: () => void;
}

/** What the panel believes about the package right now, and where it learned it. */
type Observed =
  | { source: 'unreported' }
  | { source: 'row' | 'build'; status: PpkgStatus | null; builtUtc: string | null; sizeBytes: number | null; ackPending: boolean };

export default function WavePackagePanel({ migration, onDone }: WavePackagePanelProps) {
  const { phase, retryAfterRemaining, build, reset } = usePpkgBuild(migration.migrationId, onDone);

  const waveActive = (migration.bprtStatus ?? '').toLowerCase() === 'active';
  const observed = observe(migration, phase);
  const busy = phase.kind === 'building';

  // What the ROW says, independently of what this hook is doing (FD-501).
  //
  // `observed` deliberately lets an in-session build result win over the row, so
  // it cannot answer "does a package already exist on the server". This can, and
  // two things need it. The live-polling row can go `delivered` while a build is
  // still on the wire — which is exactly the state screenshotted on staging
  // 2026-09-27, where the badge read Ready beside a spinner warning against
  // starting a second build. And a retry offered after the row has gone
  // `delivered` can only ever return `409 PACKAGE_ALREADY_DELIVERED`.
  const rowIsServable = isPpkgServable(migration.ppkgStatus);

  return (
    <div className="flex flex-col gap-3 pt-4" style={{ borderTop: `1px solid ${STROKE.subtle}` }}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3
            className="font-display text-[13px] font-semibold tracking-tight"
            style={{ color: TEXT.primary }}
          >
            Provisioning package
          </h3>
          <p className="mt-1 max-w-[62ch] font-display text-[13px]" style={{ color: TEXT.secondary }}>
            {explain(observed, waveActive)}
          </p>
        </div>
        <PackageStatusBadge observed={observed} />
      </div>

      {observed.source !== 'unreported' && (observed.builtUtc || observed.sizeBytes !== null) && (
        <dl className="flex flex-wrap gap-x-6 gap-y-1 font-display text-[13px]">
          {observed.builtUtc && (
            <div className="flex items-baseline gap-2">
              <dt style={{ color: TEXT.secondary }}>Built</dt>
              <dd style={{ color: TEXT.primary }}>
                <RelativeTime iso={observed.builtUtc} />{' '}
                <span className="font-mono text-xs">({observed.builtUtc})</span>
              </dd>
            </div>
          )}
          {observed.sizeBytes !== null && (
            <div className="flex items-baseline gap-2">
              <dt style={{ color: TEXT.secondary }}>Size</dt>
              <dd className="font-mono tabular-nums" style={{ color: TEXT.primary }}>
                {observed.sizeBytes.toLocaleString()} bytes
              </dd>
            </div>
          )}
        </dl>
      )}

      {/* One polite region. The build is a ~71s request and its completion is
          the only thing here worth announcing. */}
      <div aria-live="polite" className="sr-only">
        {busy ? 'Building the provisioning package.' : phase.kind === 'done' ? 'Package build finished.' : ''}
      </div>

      {busy && <BuildInFlight rowIsServable={rowIsServable} />}

      {phase.kind === 'done' && <BuildSucceeded ackPending={phase.result.ackPending} warning={phase.result.warning} />}

      {phase.kind === 'failed' && (
        <PackageBuildError
          error={phase.error}
          retryAfterRemaining={retryAfterRemaining}
          // Withheld once the row reports a servable package, whatever the code
          // says. A retry there is refused locally by FD-ADR-034 D3's
          // already-delivered gate before it reaches the build service, so the
          // only thing the button can produce is a second, more confusing
          // refusal. `PackageAction` hides the build button on the same
          // predicate; this is the other entry point to the same call.
          onRetry={rowIsServable ? undefined : build}
          onDismiss={reset}
        />
      )}

      <PackageAction observed={observed} waveActive={waveActive} busy={busy} onBuild={build} />
    </div>
  );
}

// ——— the build, while it is running ———————————————————————————————————

/**
 * The in-flight build (FD-501).
 *
 * The copy branches on whether the ROW already reports a servable package,
 * because both states are reachable and they mean opposite things.
 *
 * The ordinary case is the first: nothing is stored yet, the request is running,
 * and the warning against starting a second build is the point of the line.
 *
 * The second case looked like a bug on staging and is not one. The migration
 * query polls (`VITE_DEMO_REFRESH_MS` / `?live=1`), so the row can be stamped
 * `delivered` by the server while this request is still open — and the panel
 * then showed a green Ready badge, a build timestamp, AND a spinner telling the
 * operator not to start a second build. Every element was individually true and
 * the composition was incoherent, which is the kind of screen that makes someone
 * click again to find out what is really happening.
 *
 * The fix is to say the true thing rather than to hide the spinner: the package
 * exists, and this request is finishing. The old warning is dropped in that
 * branch because it no longer applies — a second build from here is refused
 * locally by FD-ADR-034 D3 and cannot produce a second credential.
 */
function BuildInFlight({ rowIsServable }: { rowIsServable: boolean }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5">
        <Spinner size="tiny" />
      </span>
      <span className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
        {rowIsServable ? (
          <>
            This campaign’s package is built and stored — the request that produced it is still
            finishing. Nothing further is needed, and nothing is at risk.
          </>
        ) : (
          <>
            Building the package on the AirBridge build service — about a minute. You can move
            around the console; this keeps running. Do not start a second build: it would take a
            second copy of this wave’s join credential.
          </>
        )}
      </span>
    </div>
  );
}

// ——— what we know, and from where —————————————————————————————————————

/**
 * The in-session build result WINS over the row, because the row is refetched
 * asynchronously and would otherwise show stale state for a beat right after a
 * build. `unreported` is a distinct third case and never collapses into "no
 * package" — see the `Migration` field comments: this Manager may not project
 * the columns at all, and rendering that as "not built" would tell an operator a
 * deliverable wave is not deliverable.
 */
function observe(migration: Migration, phase: ReturnType<typeof usePpkgBuild>['phase']): Observed {
  if (phase.kind === 'done') {
    const r = phase.result;
    return {
      source: 'build',
      status: r.ppkgStatus,
      builtUtc: r.builtUtc || null,
      sizeBytes: Number.isFinite(r.sizeBytes) ? r.sizeBytes : null,
      ackPending: r.ackPending,
    };
  }
  if (migration.ppkgStatus === undefined) return { source: 'unreported' };
  return {
    source: 'row',
    status: migration.ppkgStatus ?? null,
    builtUtc: migration.ppkgBuiltUtc ?? null,
    sizeBytes: typeof migration.ppkgSizeBytes === 'number' ? migration.ppkgSizeBytes : null,
    ackPending: migration.ppkgAckPending === true,
  };
}

function explain(observed: Observed, waveActive: boolean): string {
  if (observed.source === 'unreported') {
    return 'This deployment’s Manager does not report package state, so whether a package exists cannot be shown here. Building is still safe — one package serves a campaign for its whole life, and a second is refused.';
  }
  if (observed.status === 'delivered') {
    return 'The package is built, stored and acknowledged. Devices can be served it. One package serves a campaign for its whole life, so there is nothing further to do.';
  }
  if (observed.status === 'stored') {
    return 'The package is built, verified and held. Devices can be served it. The build service has not confirmed the delivery receipt, which changes nothing about whether this wave works.';
  }
  if (observed.status === 'building') {
    return 'A package identifier is reserved but no verified package is held, so devices will hold rather than migrate. Build it — a retry reuses the same identifier, which is what makes retrying safe.';
  }
  return waveActive
    ? 'Devices hold rather than migrate until this campaign has a package — it carries the credential they join the destination tenant with.'
    : 'A package is built from the wave’s join credential, so the wave has to be activated first.';
}

function PackageStatusBadge({ observed }: { observed: Observed }) {
  if (observed.source === 'unreported') {
    // Distinct from "no package". A neutral chip that says why, not an em-dash
    // that reads as missing data.
    return (
      <Badge appearance="tint" color="subtle">
        Not reported
      </Badge>
    );
  }
  // `stored` and `delivered` are BOTH servable (FD-320), so both are green. The
  // difference is a receipt, not a capability, and colouring `stored` as a
  // problem would teach operators to chase something that blocks nothing.
  if (isPpkgServable(observed.status)) {
    return (
      <Badge appearance="tint" color="success">
        {observed.status === 'delivered' ? 'Ready' : 'Ready (receipt pending)'}
      </Badge>
    );
  }
  if (observed.status === 'building') {
    return (
      <Badge appearance="tint" color="warning">
        Not built
      </Badge>
    );
  }
  if (observed.status === null) {
    return (
      <Badge appearance="tint" color="warning">
        Not built
      </Badge>
    );
  }
  // Verbatim, never coerced — an unrecognised value is a thing to see.
  return (
    <Badge appearance="tint" color="warning">
      {observed.status}
    </Badge>
  );
}

// ——— the action ————————————————————————————————————————————————————————

function PackageAction({
  observed,
  waveActive,
  busy,
  onBuild,
}: {
  observed: Observed;
  waveActive: boolean;
  busy: boolean;
  onBuild: () => void;
}) {
  // Terminal and expected. Offering a rebuild would be a button whose only
  // possible outcome is `409 PACKAGE_ALREADY_DELIVERED`.
  if (observed.source !== 'unreported' && observed.status === 'delivered') return null;

  // A package is built FROM the wave's credential, so with no active credential
  // the only legal outcome is `409 BPRT_NOT_ACTIVE`. Point at the real action.
  if (!waveActive) {
    return (
      <p className="font-display text-xs" style={{ color: TEXT.secondary }}>
        Activate the wave first — the package is built from the credential activation mints.
      </p>
    );
  }

  const ackPending = observed.source !== 'unreported' && observed.status === 'stored' && observed.ackPending;

  return (
    <div className="flex flex-col gap-1">
      <div>
        <Button
          appearance={ackPending ? 'secondary' : 'primary'}
          icon={
            ackPending ? (
              <RefreshCw size={16} strokeWidth={1.5} />
            ) : (
              <Hammer size={16} strokeWidth={1.5} />
            )
          }
          onClick={onBuild}
          disabled={busy}
        >
          {busy ? 'Building…' : ackPending ? 'Retry delivery receipt' : 'Build package'}
        </Button>
      </div>
      <span className="font-display text-xs" style={{ color: TEXT.secondary }}>
        {ackPending
          ? 'Retries the receipt only. It does not rebuild — AirBridge never takes a second copy of a wave’s credential to fix a receipt.'
          : 'One build, about a minute. A campaign gets one package for its whole life; a second is refused.'}
      </span>
    </div>
  );
}

function BuildSucceeded({ ackPending, warning }: { ackPending: boolean; warning?: string }) {
  if (!ackPending) {
    return (
      <MessageBar intent="success">
        <MessageBarBody>
          <MessageBarTitle>Package built</MessageBarTitle>
          It is verified, stored and acknowledged. Devices can be served it.
        </MessageBarBody>
      </MessageBar>
    );
  }
  return (
    <MessageBar intent="warning">
      <MessageBarBody>
        <MessageBarTitle>Package built — delivery receipt not confirmed</MessageBarTitle>
        {/* Deliberately NOT an error. The wave works: the package is verified and
            held, and `stored` is servable. What is missing is the build
            service's receipt. */}
        {warning ??
          'The package is stored and this campaign is usable. Retrying acknowledges the receipt; it never rebuilds.'}
      </MessageBarBody>
    </MessageBar>
  );
}

// ——— refusals ——————————————————————————————————————————————————————————

export function PackageBuildError({
  error,
  retryAfterRemaining,
  onRetry,
  onDismiss,
}: {
  error: unknown;
  retryAfterRemaining: number;
  /** Omitted when retrying cannot help — see the call site's note. */
  onRetry?: () => void;
  onDismiss: () => void;
}) {
  const code = ppkgErrorCode(error);
  const copy = refusalCopy(code, error, retryAfterRemaining);

  // A retry is offered ONLY where retrying is the recovery. `PPKG_RATE_LIMITED`
  // additionally waits out the server's ceiling, because retrying inside the
  // window is refused again and an offered-then-refused button is worse than no
  // button.
  const retryable =
    onRetry !== undefined &&
    isOperatorRetryablePpkgError(error) &&
    (code !== 'PPKG_RATE_LIMITED' || retryAfterRemaining <= 0);

  return (
    <MessageBar intent={copy.intent}>
      <MessageBarBody>
        <MessageBarTitle>{copy.title}</MessageBarTitle>
        {copy.body}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {retryable && (
            <Button size="small" appearance="secondary" onClick={onRetry}>
              Retry build
            </Button>
          )}
          <Button size="small" appearance="transparent" onClick={onDismiss}>
            Dismiss
          </Button>
          <span className="font-mono text-xs" style={{ color: TEXT.secondary }}>
            {code}
          </span>
        </div>
      </MessageBarBody>
    </MessageBar>
  );
}

type Intent = 'error' | 'warning' | 'info' | 'success';

/**
 * One entry per code. The copy is the deliverable of this task: these refusals
 * demand opposite operator actions, so collapsing any two of them into "the
 * build failed" would send someone to the wrong button at the one moment it
 * matters.
 */
function refusalCopy(
  code: ReturnType<typeof ppkgErrorCode>,
  error: unknown,
  retryAfterRemaining: number,
): { intent: Intent; title: string; body: ReactNode } {
  switch (code) {
    // ── 409: took no effect, refused identically forever ──────────────────
    case 'PACKAGE_ALREADY_DELIVERED': {
      const detail = ppkgConflictDetail(error);
      return {
        // Not an error. This is the expected answer to building twice, and the
        // campaign is in exactly the state the operator wants.
        intent: 'info',
        title: 'This campaign already has its package',
        body: (
          <>
            One package serves a migration for its whole life, so no further package will be
            produced — for this campaign, permanently. Nothing is wrong and there is nothing to
            retry. Replacing the package means a new wave, not a rebuild.
            {(detail.buildId || detail.builtUtc) && (
              <span className="font-mono text-xs">
                {' '}
                {detail.buildId ? `build ${detail.buildId}` : ''}
                {detail.builtUtc ? ` · built ${detail.builtUtc}` : ''}
              </span>
            )}
          </>
        ),
      };
    }
    case 'PACKAGE_ID_CONFLICT':
      return {
        intent: 'error',
        title: 'The package records disagree',
        body: (
          <>
            The package identifier AirBridge holds for this campaign is not the one the build
            service recorded, or it is already bound to another campaign. Nothing was built.{' '}
            <strong>Retrying will be refused identically</strong> — the two records have to be
            reconciled by a person.
          </>
        ),
      };
    case 'REBUILD_LIMIT_EXCEEDED':
      return {
        intent: 'error',
        title: 'Packages were delivered and never acknowledged',
        body: (
          <>
            {/* The single most misreadable code in the set. It looks like
                capacity and is not: it means copies of this wave's join
                credential were taken and none was acked. Acking now does not
                reopen the build. */}
            This is <strong>not a rate limit</strong>. The AirBridge build service has already
            built this campaign’s package up to its cap without a single delivery ever being
            acknowledged, which means copies of this wave’s join credential exist unaccounted
            for. <strong>This needs someone to look, not a retry</strong> — acknowledging now does
            not reopen the build. Raise it with Rubix support.
          </>
        ),
      };
    case 'PPKG_BUILD_REFUSED':
      return {
        intent: 'error',
        title: 'The build service refused the build for an unrecognized reason',
        body: (
          <>
            Nothing was built. The refusal reason is outside the agreed set, so this is a protocol
            surprise rather than a campaign problem. It is terminal — do not retry; surface it to
            Rubix support. {errorText(error)}
          </>
        ),
      };
    case 'BPRT_NOT_ACTIVE':
      return {
        intent: 'warning',
        title: 'This wave has no active credential to build from',
        body: (
          <>
            A package is built from the wave’s join credential, and this wave does not currently
            hold one. <strong>Activate the wave</strong>, then build. Nothing was sent to the build
            service.
          </>
        ),
      };
    case 'BPRT_EXPIRED':
      return {
        intent: 'warning',
        title: 'This wave’s credential has expired',
        body: (
          <>
            {/* Points at the OTHER button. Surfacing this as a build failure is
                how an operator ends up retrying the thing that cannot work. */}
            No package can be built from an expired credential.{' '}
            <strong>Re-activate the wave</strong> to mint a fresh one, then build the package.
            Retrying the build on its own will be refused identically.
          </>
        ),
      };
    case 'PPKG_TENANT_BINDING_MISMATCH':
      return {
        intent: 'error',
        title: 'The credential and the destination tenant disagree',
        body: (
          <>
            {/* FD-340 — the reason given here used to be `ATC-ADR-017` anchor 3:
                that a package carrying the right credential and a mismatched
                Authority validates cleanly and then fails past the PONR. That
                is RETRACTED, not patched, and it was false twice over. The
                package's Authority is `login.microsoftonline.com/common` —
                tenant-agnostic, so there is no per-tenant Authority for a
                credential to disagree with; and no party but Entra can say
                which tenant an opaque credential belongs to, which it can only
                do by redeeming it, which Rubix must never do.
                What the check actually gives is CALLER-CONSISTENCY: two
                independently-sourced expressions of the destination tenant,
                ours from the migration record and Rubix's from the live mint,
                must agree. The refusal is still correct and still loud — only
                the reason was wrong. Do not restore the old sentence. */}
            Two records of this campaign’s destination tenant disagree — the one AirBridge
            holds, and the one carried by the sign-in that minted this wave’s credential.{' '}
            <strong>Nothing was built, and refusing was the safeguard</strong>: a credential
            picked up from the wrong campaign or the wrong tenant would join{' '}
            <strong>this entire wave</strong> to the wrong tenant, past the point of no return.
            Re-activate the wave against this campaign’s destination tenant. {errorText(error)}
          </>
        ),
      };

    // ── 429 / 503: took no effect; retry IS the recovery ──────────────────
    case 'PPKG_RATE_LIMITED':
      return {
        intent: 'warning',
        title: 'The build service is throttling builds for this deployment',
        body: (
          <>
            Nothing was built. The retry window is a ceiling, not a suggestion — retrying sooner
            re-enters the same limit.{' '}
            {retryAfterRemaining > 0 ? (
              <>
                Retry available in{' '}
                <span className="font-mono tabular-nums">{retryAfterRemaining}s</span>.
              </>
            ) : (
              <>The window has passed; retrying is safe.</>
            )}{' '}
            A limit reached repeatedly is a conversation with Rubix support, not a reason to retry
            harder.
          </>
        ),
      };
    case 'PPKG_WORKER_UNAVAILABLE':
      return {
        intent: 'warning',
        title: 'No build worker is available',
        body: (
          <>
            {/* A hold, not a failure. Distinguished from PPKG_UNKNOWN precisely
                because this one ASSERTS nothing happened. */}
            This is a hold. Nothing was built and nothing was consumed, so{' '}
            <strong>retrying is safe</strong>. Devices will hold rather than migrate until a
            package exists — they are not stranded. If it persists, it is an AirBridge build-service
            availability problem.
          </>
        ),
      };
    case 'PPKG_NOT_CONFIGURED':
      return {
        intent: 'error',
        title: 'Package builds are not configured for this deployment',
        body: (
          <>
            The Manager has no build endpoint or build credential configured, so{' '}
            <strong>there is nothing to retry</strong> until that is set. This is a configuration
            step, not an optional one: without a package, devices hold rather than migrate.{' '}
            {errorText(error)}
          </>
        ),
      };

    // ── 502: the build ran and failed, or the answer was unusable ─────────
    case 'PPKG_BUILD_FAILED':
      return {
        intent: 'error',
        title: 'The build ran on the worker and failed',
        body: (
          <>
            A retry reuses the same package identifier and is safe. But a build that keeps failing
            is a toolchain problem that will not self-heal — after one or two attempts, raise it
            with Rubix support rather than retrying.
          </>
        ),
      };
    case 'PPKG_UNKNOWN':
      return {
        intent: 'error',
        title: 'It is not known whether a package was built',
        body: (
          <>
            {/* The most careful copy in this file. Everything else says either
                "nothing happened" or "it failed". This one says neither, and the
                honest response is a human — an automatic retry here is exactly
                how an extra copy of an unrevocable credential gets made. */}
            The build service either could not classify its own failure or never answered, so{' '}
            <strong>a package may or may not exist</strong>. This is deliberately not retried, and
            no retry is offered here.{' '}
            <strong>Reconcile with Rubix support before building this campaign again</strong> — an
            unchecked retry is how an extra copy of this wave’s join credential gets created.
          </>
        ),
      };
    case 'PPKG_AUTH_FAILED':
      return {
        intent: 'error',
        title: 'The build service did not permit this build request',
        body: (
          <>
            Nothing was built. This deployment’s build credential has been verified end to end, so
            a refusal here is usually the request rather than the credential. Check the
            deployment’s build configuration and its entitlement. {errorText(error)}
          </>
        ),
      };
    case 'PPKG_REQUEST_REJECTED':
      return {
        intent: 'error',
        title: 'The build service rejected the request before building anything',
        body: (
          <>
            Nothing was built. This is a request-shape defect rather than a transient fault, so the
            same request will be refused identically. Raise it with Rubix support. {errorText(error)}
          </>
        ),
      };
    case 'PPKG_PROTOCOL_ERROR':
      return {
        intent: 'error',
        title: 'The build service’s answer did not match the build contract',
        body: (
          <>
            Nothing was stored. A response missing the fields verification depends on cannot be
            treated as verified, so it was refused rather than accepted. Raise it with Rubix
            support.{' '}
            {errorText(error)}
          </>
        ),
      };
    case 'PPKG_HASH_MISMATCH':
      return {
        intent: 'error',
        title: 'The package did not match its digest — it was discarded',
        body: (
          <>
            The bytes the build service delivered do not hash to the value it asserted, so the
            package was{' '}
            <strong>not stored and not acknowledged</strong>. That is the correct outcome; nothing
            unverified is ever held. Raise it with Rubix support rather than retrying.
          </>
        ),
      };
    case 'PPKG_DECODE_FAILED':
      return {
        intent: 'error',
        title: 'The delivered package was not readable',
        body: (
          <>
            It could not be decoded, so it was <strong>not stored and not acknowledged</strong>.
            Raise it with Rubix support rather than retrying.
          </>
        ),
      };

    // ── 500: local, after or instead of a build ───────────────────────────
    case 'PPKG_TOO_LARGE':
      return {
        intent: 'error',
        title: 'The package is too large to hold',
        body: (
          <>
            It exceeds the custody ceiling for this deployment’s secret store, so it was{' '}
            <strong>not stored and not acknowledged</strong>. The same bytes will be too large
            again, so retrying will not help — this needs Rubix support. {errorText(error)}
          </>
        ),
      };
    case 'PPKG_STORE_FAILED':
      return {
        intent: 'error',
        title: 'The package was built and verified, but could not be stored',
        body: <>Devices will still hold. Retrying is safe and reuses the same package identifier.</>,
      };
    case 'PPKG_STATE_STAMP_FAILED':
      return {
        intent: 'warning',
        title: 'Built and stored, but the campaign record did not update',
        body: (
          <>
            The package exists and is held. Because the record was not updated,{' '}
            <strong>devices will still hold</strong>. Retry to reconcile — it reuses the same
            package identifier.
          </>
        ),
      };
    case 'PPKG_STATE_INCONSISTENT':
      return {
        intent: 'error',
        title: 'A stored package cannot be acknowledged',
        body: (
          <>
            This campaign holds a package whose build identifier was not recorded, so its receipt
            cannot be acknowledged. AirBridge <strong>will not rebuild to recover it</strong> —
            a rebuild takes delivery of a second copy of this wave’s credential. This needs an
            operator.
          </>
        ),
      };
    case 'PACKAGE_ID_RESERVE_FAILED':
      return {
        intent: 'error',
        title: 'Could not reserve a package identifier',
        body: (
          <>
            Nothing was sent to the build service and nothing was built, so retrying is safe.{' '}
            {errorText(error)}
          </>
        ),
      };
    case 'DESTINATION_TENANT_UNRESOLVED':
      return {
        intent: 'error',
        title: 'This campaign has no destination tenant',
        body: (
          <>
            There is nothing to build a package against. Check the campaign’s destination — for a
            Hybrid campaign with no override, that is the deployment’s own tenant. {errorText(error)}
          </>
        ),
      };

    default:
      return {
        intent: 'error',
        title: 'Could not build the package',
        body: (
          <>
            {/* Unclassified: no retry offered, on purpose. Guessing that an
                unknown code is safe to retry is how an extra copy of an
                unrevocable credential gets made. */}
            {errorText(error)} No retry is offered for an unrecognized failure — whether anything
            was built is not known from here.
          </>
        ),
      };
  }
}
